import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { Bundle, BundleUserPaymentType } from "@/pages/bundles/types/bundle";
import { useCreateBundle } from "../hooks/use-create-bundle";
import { useUpdateBundle } from "../hooks/use-update-bundle";
import { useUpdateBundleStatus } from "../hooks/use-update-bundle-status";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCoupons } from "../hooks/use-active-coupons";
import { Coupon } from "@/pages/coupons/types/coupon";
import { useForms } from "../hooks/use-active-forms";
import { Form as FormI } from "@/pages/forms/types/form";
import { Currency, CURRENCIES } from "@/helpers/currency-map";
import SessionTypesField from "./wizard/session-types-field";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Beaker } from "lucide-react";
import { offeringFieldLabel } from "../utils/offering-config";
import { useProducts } from "@/features/products/products-api";
import { useCompanies } from "@/features/companies/companies-api";

export const bundleSchema = z
  .object({
    standard_id: z.string().min(1, "Standard ID is required"),
    name: z.string().min(1, "Bundle name is required"),
    // onboarding | standard | reschedule (optional so the edit drawer is unaffected).
    type: z.enum(["onboarding", "standard", "reschedule"]).optional(),
    // When to collect payment, relative to scheduling sessions.
    payment_timing: z
      .enum(["BEFORE_SCHEDULING", "AFTER_SCHEDULING"])
      .optional(),
    // Card-display fields — assembled into the bundle's `metadata` JSON on submit.
    tagline: z.string().nullable().optional(),
    // Free-form bundle description (metadata.description) — reusable across the
    // app (e.g. payment-page bundle details). Not wired to any surface here.
    description: z.string().nullable().optional(),
    features: z.array(z.string()).optional(),
    isMostPopular: z.boolean().optional(),
    // Completion CTA route + label (My Subscriptions card, not-bookable state)
    // → metadata.completion_cta. Blank target = client default (product page).
    completion_cta_target: z.string().optional(),
    completion_cta_custom_path: z.string().optional(),
    completion_cta_label: z.string().optional(),
    completion_cta_requires: z.string().optional(),
    // DIVA card-CTA label overrides (both route to the product page).
    completion_cta_pending_label: z.string().optional(),
    completion_cta_report_ready_label: z.string().optional(),
    // ADHD care-team dropdown gating → metadata.care_team_actions. Which
    // manual actions the internal admin exposes for this bundle's usages:
    // two-session pack = outcome only, standalone DIVA = report only, full
    // pack = both. Both off (default) → neither item shows (non-ADHD bundles).
    care_team_assessment_outcome: z.boolean().optional(),
    care_team_diva_report: z.boolean().optional(),
    // Report format for the uploaded diagnostic report →
    // metadata.care_team_actions.report_type. 'diva' (ADHD, default) or 'ipde'
    // (Personality Disorder Care) — selects the server-side parser. Only
    // meaningful when "Provides a report" is on.
    care_team_report_type: z.string().optional(),
    // Reschedule-fee payment-page left copy (reschedule bundles only) →
    // metadata.payment_page.{description,plan_details,tnc,how_it_works}. Edited
    // as JSON (same shape/convention as the payment-page config); blank → omit,
    // and the payment app falls back to its hardcoded config.ts copy.
    reschedule_description: z.string().optional(),
    reschedule_plan_details: z.string().optional(),
    reschedule_tnc: z.string().optional(),
    reschedule_how_it_works: z.string().optional(),
    // Session types: an ordered list, one entry per session. JSON-backed so it
    // can gain fields later.
    session_types: z
      .array(
        z.object({
          priority: z.number().int().min(1),
          managed_event_id: z.string(),
          // Legacy gate — optional; derived from `participant` for back-compat.
          booked_by: z.enum(["PRIMARY_USER", "ANY_USER"]).optional(),
          // Slot key | ANY | ALL.
          participant: z.string().optional(),
          // Optional per-session step presentation (overrides the fallback card).
          title: z.string().optional(),
          description: z.string().optional(),
          image: z.string().optional(),
          // Optional override for the "Schedule next session" booking CTA label.
          book_button_label: z.string().optional(),
          // Which assessment-journey page this session unlocks when the journey
          // reaches it: 'results' (post-exploration summary) or 'report' (this
          // session is the diagnostic → report page). Absent → a normal session.
          completion_page: z.enum(["results", "report"]).optional(),
          // Optional per-session rules (config-driven booking flow).
          prerequisite: z
            .object({
              after_managed_event_id: z.string(),
              condition: z.enum(["BOOKED", "ATTENDED"]),
              blocked_reason: z.string().optional(),
              requires_metadata_key: z.string().optional(),
            })
            .optional(),
          expiry_extension: z
            .object({
              days: z.number().int().min(1),
              condition: z.enum(["BOOKED", "ATTENDED"]),
              anchor: z.enum(["SESSION_END", "SESSION_START"]).optional(),
            })
            .optional(),
        }),
      )
      .default([]),
    currency: z.string().min(1, "Bundle Currency is required"),
    // Region link (used by the create wizard; optional so the edit drawer,
    // which doesn't surface region yet, is unaffected).
    region: z.string().optional(),
    region_id: z.string().nullable().optional(),
    amount: z.number().min(0, "Amount must be non-negative"),
    reschedule_amount: z.number().min(0, "Amount must be non-negative"),
    payment_page_link: z
      .string()
      .min(1, "Payment page link is required")
      .url("Please enter a valid URL"),
    session_quantity: z.number().nullable(),
    is_expiration_in_days: z.boolean(),
    expires_in_day: z.number().nullable(),
    expires_in_month: z.number().nullable(),
    product_id: z.string().nullable(),
    company_id: z.string().nullable(),
    total_allowed_user: z.number().nullable(),
    user_payment_type: z
      .enum(["NEW_USER", "OLD_USER", "ANY"])
      .nullable()
      .optional(),
    form_ids: z.array(z.string()).default([]),
    coupon_ids: z.array(z.string()).default([]),
    status: z.enum(["ACTIVE", "INACTIVE"]),
    // Whether this bundle is publicly visible (opt-in; default false).
    show_to_public: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.is_expiration_in_days) {
        return data.expires_in_day !== null && data.expires_in_month === null;
      } else {
        return data.expires_in_month !== null && data.expires_in_day === null;
      }
    },
    {
      message: "Please provide either days or months for expiration",
      path: ["expires_in_day"],
    },
  );

type BundleFormData = z.infer<typeof bundleSchema>;

interface BundleFormProps {
  initialData?: Bundle | null;
  onClose: () => void;
  editMode?: boolean;
}

// Reusable section card so the form reads as grouped panels rather than one long list.
function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-4 min-w-0">
      <h3 className="text-lg font-medium">{title}</h3>
      {children}
    </div>
  );
}

export default function BundleForm({
  initialData,
  onClose,
  editMode,
}: BundleFormProps) {
  const form = useForm<BundleFormData, any, BundleFormData>({
    resolver: zodResolver(bundleSchema),
    defaultValues: {
      ...initialData,
      // Extract just the IDs from the coupons array if it exists
      coupon_ids: initialData?.coupons?.map((coupon) => coupon.id) || [],
      // Extract just the IDs from the forms array if it exists
      form_ids: initialData?.forms?.map((form) => form.id) || [],
      // Rest of the default values
      standard_id: initialData?.standard_id || "",
      name: initialData?.name || "",
      type: initialData?.type ?? "standard",
      payment_timing: initialData?.payment_timing ?? "BEFORE_SCHEDULING",
      tagline: (initialData?.metadata as any)?.tagline ?? null,
      description: (initialData?.metadata as any)?.description ?? "",
      features: ((initialData?.metadata as any)?.features ?? []) as string[],
      isMostPopular: (initialData?.metadata as any)?.isMostPopular ?? false,
      completion_cta_target:
        (initialData?.metadata as any)?.completion_cta?.target ?? "",
      completion_cta_custom_path:
        (initialData?.metadata as any)?.completion_cta?.custom_path ?? "",
      completion_cta_label:
        (initialData?.metadata as any)?.completion_cta?.label ?? "",
      completion_cta_requires:
        (initialData?.metadata as any)?.completion_cta?.requires ?? "",
      completion_cta_pending_label:
        (initialData?.metadata as any)?.completion_cta?.pending_label ?? "",
      completion_cta_report_ready_label:
        (initialData?.metadata as any)?.completion_cta?.report_ready_label ??
        "",
      care_team_assessment_outcome:
        (initialData?.metadata as any)?.care_team_actions?.assessment_outcome ??
        false,
      care_team_diva_report:
        (initialData?.metadata as any)?.care_team_actions?.diva_report ?? false,
      care_team_report_type:
        (initialData?.metadata as any)?.care_team_actions?.report_type ??
        "diva",
      reschedule_description: (initialData?.metadata as any)?.payment_page
        ?.description
        ? JSON.stringify(
            (initialData?.metadata as any).payment_page.description,
            null,
            2,
          )
        : "",
      reschedule_plan_details: (initialData?.metadata as any)?.payment_page
        ?.plan_details
        ? JSON.stringify(
            (initialData?.metadata as any).payment_page.plan_details,
            null,
            2,
          )
        : "",
      reschedule_tnc: (initialData?.metadata as any)?.payment_page?.tnc
        ? JSON.stringify(
            (initialData?.metadata as any).payment_page.tnc,
            null,
            2,
          )
        : "",
      reschedule_how_it_works: (initialData?.metadata as any)?.payment_page
        ?.how_it_works
        ? JSON.stringify(
            (initialData?.metadata as any).payment_page.how_it_works,
            null,
            2,
          )
        : "",
      session_types: (initialData?.session_types ??
        []) as BundleFormData["session_types"],
      currency: initialData?.currency || "INR",
      amount: initialData?.amount || 0,
      reschedule_amount: initialData?.reschedule_amount || 0,
      payment_page_link: initialData?.payment_page_link || "",
      session_quantity: initialData?.session_quantity || null,
      expires_in_day: initialData?.expires_in_day || null,
      expires_in_month: initialData?.expires_in_month || null,
      total_allowed_user: initialData?.total_allowed_user || null,
      product_id: initialData?.product_id || null,
      company_id: initialData?.company_id || null,
      user_payment_type: initialData?.user_payment_type ?? null,
      status: initialData?.status || "INACTIVE",
      is_expiration_in_days: initialData?.is_expiration_in_days || false,
      show_to_public: initialData?.show_to_public ?? false,
    },
  });

  const createBundleMutation = useCreateBundle();
  const updateBundleMutation = useUpdateBundle();
  const updateStatusMutation = useUpdateBundleStatus();
  const { data: products = [] } = useProducts();

  // Multi-participant products (participant_config with >1 slot) must collect
  // payment before scheduling — lock the selector (the server enforces this too).
  const selectedProduct = products.find(
    (p) => p.id === form.watch("product_id"),
  );
  const isMultiParticipant =
    (selectedProduct?.participant_config?.slots?.length ?? 0) > 1;
  // "Purchased for someone else" (gift / on-behalf) products: the recipient pays
  // up front then fills the product-region form — so every bundle is a standard
  // pack paid before scheduling. Hide + force both fields (UI-only).
  const forSomeoneElse =
    selectedProduct?.participant_config?.purchaser_is_participant === false;

  useEffect(() => {
    if (isMultiParticipant || forSomeoneElse) {
      form.setValue("payment_timing", "BEFORE_SCHEDULING");
    }
    if (forSomeoneElse) {
      form.setValue("type", "standard");
    }
  }, [isMultiParticipant, forSomeoneElse]);
  const { data: companies = [] } = useCompanies();
  const isSubmitting =
    createBundleMutation.isPending || updateBundleMutation.isPending;
  const [isExpirationInDays, setIsExpirationInDays] = useState<boolean>(
    initialData?.is_expiration_in_days ?? false,
  );

  // Pricing-change lifecycle: this offering's other ACTIVE price versions (from
  // GET /bundle/:id). A partial release clones an offering into rows that differ
  // only in price, so every setting on this form except the price is shared —
  // saving one row alone silently strands the others, and the newest release is
  // what new signups actually resolve to. Default ON so the safe path is default.
  const versions = initialData?.versions;
  const siblingCount = Math.max((versions?.count ?? 1) - 1, 0);
  // Only an ACTIVE row can be the source: a retired version records what was sold
  // at that price, so propagating from it would push historical copy onto the live
  // versions (the server enforces this too).
  const hasSiblings =
    !!editMode && siblingCount > 0 && initialData?.status === "ACTIVE";
  const [propagate, setPropagate] = useState(true);

  useEffect(() => {
    form.setValue("is_expiration_in_days", isExpirationInDays);
  }, [isExpirationInDays, form]);

  // handleSubmit silently skips onSubmit when schema validation fails — surface
  // the failing fields so a "dead" submit button explains itself.
  const onInvalid = (errors: Record<string, any>) => {
    console.error("[bundle-form] validation blocked submit:", errors);
    const summary = Object.entries(errors)
      .map(([field, err]) => `${field}: ${err?.message ?? "invalid"}`)
      .join(" · ");
    toast({
      title: "Fix these fields to save",
      description: summary || "Unknown validation error",
      variant: "destructive",
    });
  };

  const onSubmit = async (data: BundleFormData) => {
    console.log("[bundle-form] submitting", { editMode, id: initialData?.id });
    try {
      // Fold the card-display fields into metadata (preserving any other keys).
      const {
        tagline,
        description,
        features,
        isMostPopular,
        completion_cta_target,
        completion_cta_custom_path,
        completion_cta_label,
        completion_cta_requires,
        completion_cta_pending_label,
        completion_cta_report_ready_label,
        care_team_assessment_outcome,
        care_team_diva_report,
        care_team_report_type,
        reschedule_description,
        reschedule_plan_details,
        reschedule_tnc,
        reschedule_how_it_works,
        ...rest
      } = data;
      // Reschedule-fee payment-page copy → metadata.payment_page (reschedule
      // bundles only). Each field is optional JSON; invalid JSON aborts the save
      // with a clear message; all-blank → omit the block entirely.
      const parseCopyField = (raw: string | undefined, label: string) => {
        const trimmed = raw?.trim();
        if (!trimmed) return undefined;
        try {
          return JSON.parse(trimmed);
        } catch {
          throw new Error(`Reschedule page copy: "${label}" is not valid JSON.`);
        }
      };
      const payment_page =
        data.type === "reschedule"
          ? (() => {
              const block = {
                ...(parseCopyField(reschedule_description, "Description") !==
                undefined
                  ? { description: parseCopyField(reschedule_description, "Description") }
                  : {}),
                ...(parseCopyField(reschedule_plan_details, "Plan details") !==
                undefined
                  ? {
                      plan_details: parseCopyField(
                        reschedule_plan_details,
                        "Plan details",
                      ),
                    }
                  : {}),
                ...(parseCopyField(reschedule_tnc, "Terms & conditions") !==
                undefined
                  ? { tnc: parseCopyField(reschedule_tnc, "Terms & conditions") }
                  : {}),
                ...(parseCopyField(reschedule_how_it_works, "How it works") !==
                undefined
                  ? {
                      how_it_works: parseCopyField(
                        reschedule_how_it_works,
                        "How it works",
                      ),
                    }
                  : {}),
              };
              return Object.keys(block).length ? block : undefined;
            })()
          : undefined;
      // care_team_actions: keep the block only when at least one action is on;
      // both off → omit so non-ADHD bundles carry no gating metadata.
      const care_team_actions =
        care_team_assessment_outcome || care_team_diva_report
          ? {
              assessment_outcome: !!care_team_assessment_outcome,
              diva_report: !!care_team_diva_report,
              // 'diva' is the implicit default → only persist when a report is
              // provided AND it's the IPDE format (Personality Disorder Care).
              ...(care_team_diva_report && care_team_report_type === "ipde"
                ? { report_type: "ipde" }
                : {}),
            }
          : undefined;
      // Completion CTA: keep the block only when something is set; all blank
      // → omit it (client falls back to the product page + "View details").
      const completionTarget = completion_cta_target?.trim();
      const completionCustom = completion_cta_custom_path?.trim();
      const completionLabel = completion_cta_label?.trim();
      const completionRequires = completion_cta_requires?.trim();
      const completionPending = completion_cta_pending_label?.trim();
      const completionReportReady = completion_cta_report_ready_label?.trim();
      const completion_cta =
        completionTarget ||
        completionCustom ||
        completionLabel ||
        completionRequires ||
        completionPending ||
        completionReportReady
          ? {
              ...(completionTarget ? { target: completionTarget } : {}),
              ...(completionCustom ? { custom_path: completionCustom } : {}),
              ...(completionLabel ? { label: completionLabel } : {}),
              ...(completionRequires ? { requires: completionRequires } : {}),
              ...(completionPending
                ? { pending_label: completionPending }
                : {}),
              ...(completionReportReady
                ? { report_ready_label: completionReportReady }
                : {}),
            }
          : undefined;
      const payload = {
        ...rest,
        metadata: {
          ...((initialData?.metadata as Record<string, any>) ?? {}),
          tagline: tagline ?? null,
          description: description?.trim() ? description.trim() : null,
          features: (features ?? []).map((f) => f.trim()).filter(Boolean),
          isMostPopular: !!isMostPopular,
          ...(completion_cta
            ? { completion_cta }
            : { completion_cta: undefined }),
          care_team_actions,
          // Reschedule fee page copy (set → store, cleared/non-reschedule → drop).
          payment_page: payment_page ?? undefined,
        },
      };
      const propagating = hasSiblings && propagate;
      if (editMode && initialData?.id) {
        await updateBundleMutation.mutateAsync({
          id: initialData.id,
          data: {
            ...payload,
            // Only send the flag when there's actually something to propagate.
            ...(propagating ? { propagate_to_versions: true } : {}),
          },
        });
      } else {
        await createBundleMutation.mutateAsync(payload);
      }
      toast({
        title: "Success",
        description: propagating
          ? `Bundle updated · shared settings applied to ${siblingCount} other price version${
              siblingCount === 1 ? "" : "s"
            }`
          : `Bundle ${editMode ? "updated" : "created"} successfully`,
      });
      onClose();
    } catch (error: any) {
      console.error("Form submission error:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save bundle",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onInvalid)}
        className="space-y-6 p-6 overflow-x-hidden min-w-0"
      >
        {/* Header */}
        <div className="bg-gray-100 rounded-lg p-4 space-y-2">
          <h2 className="text-2xl font-semibold">
            {editMode ? "Edit" : "Add"} Bundle
          </h2>
          {/* Standard ID is the immutable offering key — shown as a tag, never
              editable. */}
          {form.getValues("standard_id") && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Standard ID</span>
              <Badge variant="secondary" className="font-mono">
                {form.getValues("standard_id")}
              </Badge>
            </div>
          )}
        </div>

        {/* Shared-settings notice. Only when the offering has more than one active
            price version (i.e. a partial release is live). Everything on this form
            except the price describes the offering, so saving a single row leaves
            the other versions behind — and the newest release is the one new
            signups resolve to, so the stale copy is the one most people see. */}
        {hasSiblings && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Beaker className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-amber-900">
                  This offering has {versions?.count} active price versions
                  {versions?.isBaseline
                    ? " — you're editing the baseline."
                    : " — you're editing an experimental release."}
                </p>
                <p className="text-xs text-amber-800">
                  Only the price differs between versions. Every other setting
                  below is shared, so it should be saved to all of them.
                </p>
                {!!versions?.drift.length && (
                  <p className="text-xs text-amber-800">
                    <span className="font-medium">
                      Already out of sync ({versions.drift.length}):
                    </span>{" "}
                    {versions.drift.map(offeringFieldLabel).join(", ")}. Saving
                    with the box below ticked will bring them back in line.
                  </p>
                )}
              </div>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={propagate}
                onCheckedChange={(v) => setPropagate(v === true)}
                className="mt-0.5"
              />
              <span className="text-sm text-amber-900">
                Apply shared settings to the other {siblingCount} price version
                {siblingCount === 1 ? "" : "s"}
                <span className="block text-xs text-amber-700">
                  Price, release date and status are never copied. Retired
                  versions are left untouched.
                </span>
              </span>
            </label>
          </div>
        )}

        {/* Main (2/3) + settings sidebar (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* ---------- Main column ---------- */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            <SectionCard title="Basic Information">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Bundle Name <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Bundle Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value: Currency) => field.onChange(value)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency">
                              {field.value}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CURRENCIES.map((currency) => (
                            <SelectItem key={currency.value} value={currency.value}>
                              {currency.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Amount <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Amount"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="reschedule_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Reschedule Amount <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Reschedule Amount"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="sm:col-span-2">
                  <FormField
                    control={form.control}
                    name="payment_page_link"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Payment Page Link <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="https://..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Sessions">
              <SessionTypesField form={form} />
            </SectionCard>

            {form.watch("type") === "reschedule" && (
              <SectionCard title="Reschedule page copy">
                <p className="text-xs text-gray-500 -mt-2">
                  Left-side copy for the reschedule fee payment page. Each field is
                  optional JSON matching the payment-page shape; blank fields fall
                  back to the app&apos;s hardcoded copy.
                </p>
                {(
                  [
                    [
                      "reschedule_description",
                      "Description",
                      '{ "title": "…", "note": "…" }',
                    ],
                    [
                      "reschedule_plan_details",
                      "Plan details",
                      '{ "title": "This plan has:", "items": ["…", "…"] }',
                    ],
                    [
                      "reschedule_tnc",
                      "Terms & conditions",
                      '{ "validity": "…", "reschedulePolicy": ["…"], "refundPolicy": ["…"], "note": "…" }',
                    ],
                    [
                      "reschedule_how_it_works",
                      "How it works",
                      '{ "title": "…", "items": ["…"] }',
                    ],
                  ] as [
                    | "reschedule_description"
                    | "reschedule_plan_details"
                    | "reschedule_tnc"
                    | "reschedule_how_it_works",
                    string,
                    string,
                  ][]
                ).map(([name, label, placeholder]) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{label}</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={4}
                            placeholder={placeholder}
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </SectionCard>
            )}

            <SectionCard title="Completion CTA (My Subscriptions card)">
              <p className="text-xs text-gray-500 mb-2">
                The card&apos;s button once the pack has no session left to book
                (the &quot;View details&quot; state) — its label and where it
                goes. Leave blank to keep the defaults (&quot;View details&quot;
                → product page).
              </p>
              <FormField
                control={form.control}
                name="completion_cta_label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Button label</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="View details"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="completion_cta_pending_label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      DIVA — report-pending label{" "}
                      <span className="text-gray-400 font-normal">
                        (assessment done, report not saved yet)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Next Steps"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="completion_cta_report_ready_label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      DIVA — report-ready label{" "}
                      <span className="text-gray-400 font-normal">
                        (report saved, debrief not booked — routes to the
                        product page)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Download your report and schedule your next session"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="completion_cta_target"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Destination</FormLabel>
                    <FormControl>
                      <select
                        className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm bg-white"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="">Default (product page)</option>
                        <option value="PRODUCT">Product page</option>
                        <option value="RESULTS">Results page</option>
                        <option value="REPORT">Report page</option>
                        <option value="SCHEDULING">Scheduling (book)</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="completion_cta_custom_path"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Custom path override{" "}
                      <span className="text-gray-400 font-normal">
                        (optional — wins over Destination)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="/product/{productId}/results?usage_id={usageId}"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <p className="text-xs text-gray-400 mt-1">
                      Placeholders: {"{productId}"}, {"{usageId}"},{" "}
                      {"{bundleStandardId}"}, {"{region}"}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="completion_cta_requires"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Only route here once published{" "}
                      <span className="text-gray-400 font-normal">
                        (else the product page)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <select
                        className="w-full h-9 rounded-md border border-gray-200 px-2 text-sm bg-white"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <option value="">Always (no gate)</option>
                        <option value="assessmentOutcome">
                          Exploration outcome recorded
                        </option>
                        <option value="divaReportUrl">
                          DIVA report uploaded
                        </option>
                        <option value="divaOutcome">
                          DIVA outcome recorded
                        </option>
                      </select>
                    </FormControl>
                    <p className="text-xs text-gray-400 mt-1">
                      Until the care team publishes this, the button keeps its
                      label but goes to the product page instead.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SectionCard>

            <SectionCard title="Care team actions (ADHD assessment)">
              <p className="text-xs text-gray-500 mb-2">
                Which manual care-team items appear on this bundle&apos;s usages
                in the internal admin. Leave both off for non-ADHD bundles.
              </p>
              <FormField
                control={form.control}
                name="care_team_assessment_outcome"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Record exploration outcome
                      </FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Two-session pack &amp; full pack.
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="care_team_diva_report"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">DIVA report</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Full pack &amp; standalone DIVA.
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {form.watch("care_team_diva_report") && (
                <FormField
                  control={form.control}
                  name="care_team_report_type"
                  render={({ field }) => (
                    <FormItem className="rounded-lg border p-4">
                      <FormLabel className="text-base">Report type</FormLabel>
                      <p className="text-xs text-muted-foreground mb-2">
                        Which parser reads the uploaded report PDF. DIVA (ADHD)
                        or IPDE (Personality Disorder Care).
                      </p>
                      <Select
                        value={field.value || "diva"}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="diva">DIVA (ADHD)</SelectItem>
                          <SelectItem value="ipde">
                            IPDE (Personality Disorder)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              )}
            </SectionCard>

            <SectionCard title="Card Display">
              <FormField
                control={form.control}
                name="tagline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tagline</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. BEST VALUE"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Description{" "}
                      <span className="text-gray-400 font-normal">
                        (reusable across the app)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. A structured clinical exploration of your focus and history"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="features"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Features (one per line)</FormLabel>
                    <FormControl>
                      <textarea
                        className="w-full min-h-[80px] rounded-md border border-gray-200 p-2 text-sm"
                        placeholder={"8 therapy sessions\nFlexible scheduling\nPriority support"}
                        value={(field.value ?? []).join("\n")}
                        onChange={(e) => field.onChange(e.target.value.split("\n"))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SectionCard>

            <SectionCard title="Associated Items">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
                {/* Forms Multi-select */}
                <FormField
                  control={form.control}
                  name="form_ids"
                  render={({ field }) => {
                    const { data: formsData } = useForms();
                    const forms = formsData?.forms || [];

                    return (
                      <FormItem>
                        <FormLabel>Forms</FormLabel>
                        <FormControl>
                          <div className="space-y-2">
                            <Select
                              onValueChange={(value) => {
                                const currentValues = field.value || [];
                                if (!currentValues.includes(value)) {
                                  field.onChange([...currentValues, value]);
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select Forms" />
                              </SelectTrigger>
                              <SelectContent>
                                {forms.map((formItem: FormI) => (
                                  <SelectItem key={formItem.id} value={formItem.id}>
                                    {formItem.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <div className="flex flex-wrap gap-2">
                              {field.value?.map((selectedId) => {
                                const selectedForm = forms.find(
                                  (f: FormI) => f.id === selectedId,
                                );
                                return (
                                  selectedForm && (
                                    <div
                                      key={selectedId}
                                      className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"
                                    >
                                      <span className="text-sm">
                                        {selectedForm.name}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          field.onChange(
                                            field.value.filter(
                                              (id) => id !== selectedId,
                                            ),
                                          );
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  )
                                );
                              })}
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                {/* Coupons Multi-select */}
                <FormField
                  control={form.control}
                  name="coupon_ids"
                  render={({ field }) => {
                    const { data: couponsData } = useCoupons();
                    const coupons = couponsData?.coupons || [];

                    return (
                      <FormItem>
                        <FormLabel>Coupons</FormLabel>
                        <FormControl>
                          <div className="space-y-2">
                            <Select
                              onValueChange={(value) => {
                                const currentValues = field.value || [];
                                if (!currentValues.includes(value)) {
                                  field.onChange([...currentValues, value]);
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select Coupons" />
                              </SelectTrigger>
                              <SelectContent>
                                {coupons.map((coupon: Coupon) => (
                                  <SelectItem key={coupon.id} value={coupon.id}>
                                    {coupon.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <div className="flex flex-wrap gap-2">
                              {field.value?.map((selectedId) => {
                                const selectedCoupon = coupons.find(
                                  (c: Coupon) => c.id === selectedId,
                                );
                                return (
                                  selectedCoupon && (
                                    <div
                                      key={selectedId}
                                      className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full"
                                    >
                                      <span className="text-sm">
                                        {selectedCoupon.name}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          field.onChange(
                                            field.value.filter(
                                              (id) => id !== selectedId,
                                            ),
                                          );
                                        }}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  )
                                );
                              })}
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </SectionCard>
          </div>

          {/* ---------- Settings sidebar (1/3) ---------- */}
          <div className="lg:col-span-1 space-y-6 min-w-0">
            <SectionCard title="Status & Visibility">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bundle Status</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value: "ACTIVE" | "INACTIVE") => {
                        field.onChange(value);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status">
                            {field.value}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="INACTIVE">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="show_to_public"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Show to public</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Whether this bundle is publicly visible.
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isMostPopular"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <FormLabel className="text-base">Most popular</FormLabel>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </SectionCard>

            <SectionCard title="Type & Payment">
              {forSomeoneElse ? (
                <p className="text-xs text-muted-foreground">
                  "For someone else" products are always standard bundles paid
                  before scheduling — type &amp; payment timing are fixed.
                </p>
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Bundle type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="onboarding">
                              Onboarding
                            </SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="reschedule">
                              Reschedule
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="payment_timing"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment timing</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={isMultiParticipant}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="When to take payment" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="BEFORE_SCHEDULING">
                              Pay before scheduling
                            </SelectItem>
                            <SelectItem value="AFTER_SCHEDULING">
                              Pay after scheduling
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {isMultiParticipant ? (
                          <p className="text-xs text-muted-foreground">
                            Multi-participant products must be paid before
                            scheduling.
                          </p>
                        ) : null}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="total_allowed_user"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Total Allowed Users</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Maximum users allowed"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SectionCard>

            <SectionCard title="Expiration">
              <FormField
                control={form.control}
                name="is_expiration_in_days"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Expiration in Days
                      </FormLabel>
                    </div>
                    <FormControl>
                      <Switch
                        checked={isExpirationInDays}
                        onCheckedChange={(checked) => {
                          setIsExpirationInDays(checked);
                          field.onChange(checked);
                          if (checked) {
                            form.setValue("expires_in_month", null);
                          } else {
                            form.setValue("expires_in_day", null);
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expires_in_day"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expires in Days</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Number of days"
                        disabled={!isExpirationInDays}
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expires_in_month"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expires in Months</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Number of months"
                        disabled={isExpirationInDays}
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : null,
                          )
                        }
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SectionCard>

            <SectionCard title="Relationships">
              <FormField
                control={form.control}
                name="product_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product</FormLabel>
                    <Select
                      value={field.value ?? "NONE"}
                      onValueChange={(value) =>
                        field.onChange(value === "NONE" ? null : value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="NONE">No product</SelectItem>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="company_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <Select
                      value={field.value ?? "NONE"}
                      onValueChange={(value) =>
                        field.onChange(value === "NONE" ? null : value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a company" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="NONE">No company</SelectItem>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SectionCard>

            <SectionCard title="User Payment Type">
              <FormField
                control={form.control}
                name="user_payment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>User Payment Type</FormLabel>
                    <Select
                      value={field.value ?? "NONE"}
                      onValueChange={(value) => {
                        field.onChange(value === "NONE" ? null : (value as BundleUserPaymentType));
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user payment type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="NONE">None</SelectItem>
                        <SelectItem value="NEW_USER">New User</SelectItem>
                        <SelectItem value="OLD_USER">Old User</SelectItem>
                        <SelectItem value="ANY">Any</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SectionCard>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span>Loading...</span>
            ) : editMode ? (
              "Update"
            ) : (
              "Create"
            )}{" "}
            Bundle
          </Button>
        </div>
      </form>
    </Form>
  );
}
